/* eslint-disable max-lines */
import { get } from 'lodash';

import app from '../app';
import { getProperty, setProperty, deleteProperty } from '../../scripts/utils';
import { ERegions } from '../../../../shared/constants';
import { selectIsJKValid } from '../../../../shared/selectors/jk';

const events = {
  isShortFormChange: 'addForm.layout.isShortFormChange',
};

// регионы, в которых доступен лэйаут с короткой формой
const shortLayoutLocationIds = window.addFormState.bsCenterRegions;

// регионы, в которых доступен лэйаут с короткой формой для новостроек
const jkShortLayoutLocationIds = [
  ERegions.moscow,
  ERegions.moscowRegion,
  ERegions.spb,
  ERegions.spbRegion,
  ERegions.bashkortostan,
  ERegions.nizhniyNovgorodRegion,
  ERegions.rostovRegion,
  ERegions.samaraRegion,
  ERegions.tatarstan,
];

app.service('addFormLayoutService', [
  '$rootScope',
  'bsCenterService',
  'btiService',
  'jkService',
  'addFormService',
  'addressService',
  'analyticsService',
  'categoryService',
  'isSimpleFormEnabled',
  'coworkingService',
  'store',
  function (
    $rootScope,
    bsCenterService,
    btiService,
    jkService,
    addFormService,
    addressService,
    analyticsService,
    categoryService,
    isSimpleFormEnabled,
    coworkingService,
    store,
  ) {
    this.init = () => {
      // активен ли короткий лэйаут, true - доступны и короткая и полная формы, false - только полная форма (классический вид формы)
      this.isShortLayout = false;

      // режим короткого лэйаута - bsCenter/bti/jk, влияет на поведение формы (вид блока адреса, способ определения привязки здания и др.)
      this.shortLayoutMode = null;

      // включена ли короткая форма (имеет смысл только на коротком лэйауте)
      this.isShortForm = false;

      this.revertValues = null;

      jkService.onChangeJk(() => {
        this.setLayout();
      });
    };

    this.init();

    this.setLayoutSettings = ({ isShortLayout, shortLayoutMode, isShortForm }) => {
      this.isShortLayout = isShortLayout;
      this.shortLayoutMode = shortLayoutMode;
      this.isShortForm = isShortForm;
    };

    /**
     * По хорошему мы должны знать что заполнили именно данные БТИ
     * Но так как мы не храним в стейте формы id дома для которого заполнили данные БТИ
     * Считаем что при редактировании данные для БТИ были заполнены и можно схлопнуть форму
     */
    const btiProcessed = () => btiService.isBtiProcessed() || get(addFormService.ad, 'id');

    /**
     * Установить формат формы - короткая/полная или только полная в зависимости от региона и категории.
     * Для первого случая определяем нужно ли показывать короткую форму по наличию привязки здания.
     * Для ЖК короткий лэйаут и короткая форма включаются только при выборе корпуса ЖК из списка
     */
    this.setLayout = () => {
      if (isSimpleFormEnabled()) {
        this.isShortLayout = false;
        this.shortLayoutMode = null;
        this.isShortForm = false;

        return;
      }

      const state = store.getState();

      /** Определяет валидность введенного ЖК для коммерческих категорий с бизнес-центрами */
      const isJKValid = selectIsJKValid(state);

      const allowedLocations = jkService.isJkCategory() ? jkShortLayoutLocationIds : shortLayoutLocationIds;
      const isShortLayoutLocation = allowedLocations.some(id => addressService.isLocation(id));
      const isBS = bsCenterService.isBSCategory();
      const isHouse =
        btiService.isBtiCategory() &&
        addressService.isHouse() &&
        isShortFormAvailableForBti() &&
        !bsCenterService.isBSCenterAttached() &&
        btiProcessed();

      const isJkSelected = (jkService.isJkCategory() || jkService.isJkSecondaryCategory()) && jkService.isJkSelected();
      const isCoworking = coworkingService.isCoworkingCategory();

      const oldIsShortLayout = this.isShortLayout;
      const oldIsShortForm = this.isShortForm;

      this.isShortLayout = isShortLayoutLocation && (isBS || isHouse || isJkSelected || isCoworking);

      if (this.isShortLayout) {
        if (isBS) {
          this.shortLayoutMode = 'bsCenter';
          /* Дает пользователю переключиться на короткую форму, когда:
        - или Привязан БЦ
        - или БТИ (адрес до дома)
        - или ЖК/Корпус выбраны и есть в базе
       */
          this.isShortForm = bsCenterService.isBSCenterAttached() || btiProcessed() || isJKValid;
        } else if (isJkSelected) {
          this.shortLayoutMode = 'jk';
          this.isShortForm = isJKValid && !jkService.hasProblems();
        } else if (isHouse) {
          this.shortLayoutMode = 'bti';
          this.isShortForm = true;
        } else if (isCoworking) {
          const coworkingLayoutSettings = coworkingService.getLayoutSettings();

          this.setLayoutSettings(coworkingLayoutSettings);
        }
      } else {
        this.shortLayoutMode = null;
        this.isShortForm = false;
      }

      if (this.isShortLayout !== oldIsShortLayout || this.isShortForm !== oldIsShortForm) {
        triggerIsShortFormChange(this.isShortForm, oldIsShortForm);
      }
    };

    // переключится на короткую или полную форму
    this.setShortForm = (short = true, explicit = false) => {
      if (isSimpleFormEnabled()) {
        this.shortLayoutMode = null;
        this.isShortForm = false;

        return;
      }
      const isCoworking = coworkingService.isCoworkingCategory();

      if (isCoworking && !short) {
        coworkingService.clearCoworking();
      }

      // У коворкингов нет короткой формы
      if (!isCoworking && !this.isShortLayout) {
        throw new Error('Чтобы переключиться на короткую форму, нужно установить коротокий формат формы');
      }
      const oldIsShortForm = this.isShortForm;
      this.isShortForm = short;
      if (oldIsShortForm !== short) {
        triggerIsShortFormChange(short, oldIsShortForm, explicit);

        if (short) {
          analyticsService.setShortForm();
        } else {
          analyticsService.setLongForm();
        }
      }
    };

    // доступна ли короткая форма. этот метод определяет будет ли показана заявка на добавление здания в короткой форме.
    // Для БЦ определяется наличием привязки здания к объявлению,

    // Устарело: для БТИ - доступна всегда, т.к. короткий лэйаут в жилой недв включается только при вводе адреса до дома
    // БТИ заполняется в жилой и коммерческой, для категории коммерческая/здание форму не надо схлопывать
    const isShortFormAvailableForBti = () => {
      // для коворкингов показываем только одну форму
      if (coworkingService.isCoworkingCategory()) {
        return false;
      }

      const { objectType } = categoryService.getInfoByCategory(get(addFormService, 'ad.category'));

      return objectType !== 'building';
    };
    // для ЖК короткая форма всегда доступна т.к. короткий лэйаут в новостройках включается только при выборе ЖК из каталога (форма-заявка у ЖК своя)
    this.isShortFormAvailable = () => {
      const state = store.getState();

      const isJKValid = selectIsJKValid(state);

      if (this.shortLayoutMode === 'bsCenter') {
        /* Схлопываем гео сами, когда:
        - или Привязан БЦ
        - или БТИ (адрес до дома)
        - или ЖК/Корпус выбраны и есть в базе
       */
        return bsCenterService.isBSCenterAttached() || btiProcessed() || isJKValid;
      } else if (this.shortLayoutMode === 'bti') {
        return isShortFormAvailableForBti();
      } else if (this.shortLayoutMode === 'jk') {
        return isJKValid;
      }

      if (this.shortLayoutMode === 'coworking') {
        return true;
      }

      return false;
    };

    /**
     * подписаться на изменение вида формы с короткого на полный и наоборот
     * @param handler обработчик вида function(newValue, oldValue){}
     * @return функция для отписки
     */
    this.onIsShortFormChange = handler => $rootScope.$on(events.isShortFormChange, (event, args) => handler(args));
    const triggerIsShortFormChange = (isShortForm, oldIsShortForm, explicit) =>
      $rootScope.$emit(events.isShortFormChange, { isShortForm, oldIsShortForm, explicit });

    /**
     * Описание полей блока "О здании" и др, для которых есть валидация, значения этих полей сохраняются при открытии на редактирование
     * и при привязке здания. При переключении на короткую форму, откатываем эти поля к сохраненным значения, если пользователь
     * ввел невалидные значения.
     * Формат описания поля:
     * - строка - для простых случаев, если модель и название поля совпадают;
     * - объект со свойствами model (модель, относительно объекта ad), fieldNames (названия полей формы, по которымпроверяется валидность модели)
     */
    const revertFields = {
      common: [
        {
          model: 'geo.undergrounds',
          fieldNames: [
            'geo.undergrounds.0.id',
            'geo.undergrounds.1.id',
            'geo.undergrounds.2.id',
            'geo.undergrounds.0.time',
            'geo.undergrounds.1.time',
            'geo.undergrounds.2.time',
          ],
        },
        // возможно тут надо добавить шоссе?
      ],
      bsCenter: [
        'building.buildYear',
        'building.totalArea',
        'land.area',
        {
          model: 'building.liftTypes',
          fieldNames: ['building.liftTypes.0', 'building.liftTypes.1', 'building.liftTypes.2'],
        },
        { model: 'building.openingHours', fieldNames: ['building.openingHours.to', 'building.openingHours.from'] },
      ],
      bti: ['building.buildYear', 'building.ceilingHeight'],
      // для ЖК адрес тоже может быть невалидным на коротком лэйауте, поэтому откатываем и его
      jk: [
        'geo.userInput',
        { model: 'geo.address', fieldNames: ['geo.userInput'] },
        { model: 'geo.coordinates', fieldNames: ['geo.userInput'] },
      ],
      coworking: [],
    };

    // значения полей, которые нужно откатывать
    let revertValues = {};

    const getRevertFields = () => [...revertFields.common, ...revertFields[this.shortLayoutMode]];

    /**
     * Сохранить значения полей для дальнейшего откатывания, разные поля сохраняются в зависимости от режима (БЦ, БТИ, ЖК)
     * в момент привязки здания и открытия на редактирование
     */
    this.setRevertValues = () => {
      const isCoworking = coworkingService.isCoworkingCategory();

      // У коворкингов нет короткой формы
      if (!isCoworking && !this.isShortLayout) {
        throw new Error('addFormLayoutService.setRevertValues: Не включен короткий лэйаут');
      }
      revertValues = {};
      getRevertFields().forEach(f => {
        const model = angular.isString(f) ? f : f.model;
        const value = getProperty(addFormService.ad, model);
        if (value) {
          revertValues[model] = angular.copy(value);
        }
      });
    };

    /**
     * Откатить поля к сохраненным ранее значениям, вызывается при переключении на короткую форму, т.к. мы скрываем часть формы
     * и не можем оставить невалидные значения в этой части.
     * @param form
     */
    this.revertInvalidFields = form => {
      const isCoworking = coworkingService.isCoworkingCategory();

      if (!isCoworking && !this.isShortLayout) {
        throw new Error('addFormLayoutService.setRevertValues: Не включен короткий лэйаут');
      }
      getRevertFields(this.shortLayoutMode).forEach(f => {
        const model = angular.isString(f) ? f : f.model;
        const fieldNames = angular.isString(f) ? [f] : f.fieldNames;
        const isValid = fieldNames
          .map(n => !form[n] || form[n].$valid)
          .reduce((result, curValid) => result && curValid, true);
        if (!isValid) {
          const value = revertValues[model];
          if (value) {
            setProperty(addFormService.ad, model, angular.copy(value));
          } else {
            deleteProperty(addFormService.ad, model);
          }
        }
      });
    };

    let sendedRequests = [];
    this.appendToSendedRequests = address => {
      sendedRequests.push(address);
    };

    this.isReqestSended = address => {
      return sendedRequests.indexOf(address) !== -1;
    };
  },
]);
